// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
/* eslint-disable -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved. */
import { useState, useEffect, useRef, useMemo } from "react";
import { v4 } from "uuid";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material";
import { DataGrid, GridRowModes } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { useStateRef } from "@/hooks/useStateRef";
import { useDynamicTranslation } from "@/hooks/useDynamicTranslation";
import LoadingOverlay from "./loading-overlay";
import renderCellExpand from "./grid-cell-expand";
import { DefaultPagination, UPTPagination } from "./pagination";
import * as Actions from "./editing-actions";
import { assignColumnDefaults, handleInitialPagination } from "./helpers";
import { Dialog } from "../dialog";
import { DefaultToolbar, UPTToolbar } from "./toolbar";
import { DefaultEditForm } from "./edit-form";
/**
 * @param {{
 * columns: Array<{
 *    field: String,
 *    [align]: "left" | "right" | "center",
 *    headerName: String,
 *    [headerAlign]: "left" | "right" | "center",
 *    [type]: "number" | "boolean" | "currency",
 *    description?: String,
 *    width?: Number,
 *    flex?: Number,
 *    valueGetter?: Function,
 *    renderCell?: Function,
 *    editable?: Boolean
 * }>,
 * rows: Array<any>
 * [remotePaging]: {currentPage: Number, pagesize: Number, totalCount: Number},
 * [loadData]: (arg: {index: Number, size: Number, name: String}) => any | (arg: {index: Number, size: Number, name: String}) => Promise<any>,
 * [editMode]: "inline" | "modal" | "redirect",
 * [hideToolBar]: Boolean,
 * [hideSearchBox]: Boolean,
 * [paginatinMode]: "upt" | "default",
 * [height]: String,
 * [density]: "compact" | "standard" | "comfortable",
 * [autoHeight]: Boolean,
 * [disableInitialLoad]: Boolean,
 * [getRowId]: Function,
 * [checkboxSelection]: Boolean,
 * [isRowSelectable]: (params: import("@mui/x-data-grid").GridRowParams<any>) => Boolean,
 * [onCellEditCommit]: import("@mui/x-data-grid").GridEventListener<"cellEditCommit">,
 * [initialPagination]: {size: Number, index: Number},
 * [hideFilterButton]: Boolean,
 * [hideExportButton]: Boolean,
 * [disableLoadingAnimation]: Boolean,
 * [onRowSave]: (newRow: any, {oldRow: any}) => Promise<any>
 * }}
 */

export function DataTable({
  columns,
  rows,
  remotePaging,
  pagination = true,
  rowsPerPageOptions = [15, 30, 50],
  loadData,
  disableSelectAllCheckbox = false,
  disableColumnMenu = false,
  disableSelectionOnClick = true,
  disablePagination = false,
  editMode,
  hideToolBar,

  hideSearchBox,
  height,
  density = "standard",
  autoHeight,
  disableInitialLoad = false,
  getRowId,
  checkboxSelection = false,
  onRowClick,
  onRowSelection,
  isRowSelectable,
  onCellEditCommit,
  initialPagination,
  hideFilterButton = false,
  hideExportButton = false,
  disableLoadingAnimation,
  hideFooterSelectedRowCount,
  maxSelectableRowCount,
  onRowSave,
  EditForm,
  paginationModel = "default",
  FilterForm,
  toolbarModel = "default",
  footerLeftButton,
  footerRightButton,
  dataTableTitle,
}) {
  const theme = useTheme();
  const { t, lang } = useDynamicTranslation("shared");
  const isFirstLoad = useRef(true);
  const [filterButtonEl, setFilterButtonEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rowModesModel, setRowModesModel] = useState({});
  const [tableFilters, setTableFilters] = useState();
  const [tablePaging, setTablePaging, tablePagingRef] = useStateRef(
    handleInitialPagination(initialPagination)
  );
  const [gridRows, setGridRows] = useState(rows || 0);
  const [editingDialog, setEditingDialog] = useState({ row: null, isUpdated: false });
  const mobileMediaQuery = theme.breakpoints.down("md");

  const { inputText, setInputText } = useDebouncedSearch((text) => {
    setTablePaging((prev) => ({
      ...prev,
      page: 1,
      status: "inside",
    }));
    getRows(text);
  });

  const getRows = async (text) => {
    const { page: index, pageSize: size } = tablePagingRef.current;

    if (loadData) {
      // start spinner
      if (!disableLoadingAnimation) {
        setLoading(true);
      }

      window.addEventListener("filterFormToTable", (e) => {
        setTableFilters(e.detail);
      });

      await loadData({
        index,
        size,
        name: text ?? inputText,
        filter: tableFilters,
      });
    } else {
      const newRows = [...rows].slice((index - 1) * size, index * size);
      setGridRows(newRows);
    }
  };

  // stop spinner
  useEffect(() => {
    setLoading(false);
    setGridRows(rows);
  }, [rows]);

  // call getRow in initial render if disableInitialLoad is false and on page change
  useEffect(() => {
    if (disableInitialLoad && isFirstLoad.current) {
      isFirstLoad.current = false;

      return;
    }

    getRows(inputText);
  }, [tablePaging, tableFilters, inputText]);

  const handleSaveClick = (id) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelEditingClick = (id) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleStartEditingClick = (id) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const gridColumns = useMemo(() => {
    const cols = assignColumnDefaults(columns);

    if (editMode && !cols.some((item) => item.field === "actions")) {
      cols.push({
        field: "actions",
        type: "actions",
        headerName: t("actions"),
        getActions: (params) => {
          const isEditingCurrentRow = rowModesModel[params.id]?.mode === GridRowModes.Edit;

          if (editMode === "redirect") {
            return [<Actions.RedirectAction key={v4()} id={params.id} />];
          }

          if (editMode === "modal") {
            return [
              <Actions.StartEditingAction
                key={v4()}
                onClick={() => setEditingDialog({ row: params.row, isUpdated: false })}
              />,
            ];
          }

          return isEditingCurrentRow
            ? [
                <Actions.SaveAction key={v4()} onClick={() => handleSaveClick(params.id)} />,
                <Actions.CancelEditingAction
                  key={v4()}
                  onClick={() => handleCancelEditingClick(params.id)}
                />,
              ]
            : [
                <Actions.StartEditingAction
                  key={v4()}
                  onClick={() => handleStartEditingClick(params.id)}
                />,
              ];
        },
      });
    }

    return cols;
  }, [columns, rowModesModel, lang]);

  let toolbarProps = {
    hideFilterButton,
    setFilterButtonEl,
    hideExportButton,
    getRows,
    FilterForm,
    dataTableTitle,
    tableFilters,
    setTableFilters,
    loadData,
  };

  if (!hideSearchBox) {
    toolbarProps = {
      ...toolbarProps,
      searchBoxProps: {
        value: inputText,
        onChange: (event) => setInputText(event.target.value),
        clearSearch: () => {
          setInputText("");
        },
      },
    };
  }

  const handleRowUpdate = async (newRow) => {
    await onRowSave(newRow);
    setEditingDialog({ ...editingDialog, isUpdated: true });
  };

  const handleCloseEditingDialog = () => {
    setEditingDialog({ row: null, isUpdated: false });

    if (editingDialog.isUpdated) {
      getRows();
    }
  };

  const processRowUpdate = (newRow, oldRow) => {
    const updatedRow = { ...newRow };
    setLoading(true);

    return new Promise((resolve) => {
      onRowSave(updatedRow, { oldRow })
        .then(() => resolve(updatedRow))
        .catch(() => {
          // setErrorMessage(err.response.data.result);
          resolve(oldRow);
        })
        .finally(() => setLoading(false));
    });
  };

  const handleOnRowEditModeChange = (_, event) => {
    // disable mui default behaviour: activating/deactivating edit mode with double click
    event.defaultMuiPrevented = true;
  };

  const PaginationComponent = paginationModel === "default" ? DefaultPagination : UPTPagination;
  const PaginationModel = toolbarModel === "default" ? DefaultToolbar : UPTToolbar;

  const [selectedItems, setSelectedItems] = useState([]);

  return (
    <Box
      sx={{
        height: !autoHeight ? (height ?? "calc(100vh - 230px)") : "auto",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <DataGrid
        disableColumnMenu
        disableSelectionOnClick={disableSelectionOnClick}
        keepNonExistentRowsSelected
        columns={gridColumns}
        rows={gridRows}
        onRowClick={onRowClick}
        rowCount={remotePaging?.totalCount ?? (rows ? rows.length : 0)}
        pagination={pagination}
        paginationMode="server"
        page={tablePaging.page - 1}
        pageSize={tablePaging?.pageSize}
        hideFooterPagination
        loading={loading}
        autoHeight={autoHeight}
        density={density}
        getRowId={getRowId}
        hideExportButton={hideExportButton}
        checkboxSelection={checkboxSelection}
        hideFooterSelectedRowCount
        disableColumnSorting
        onSelectionModelChange={(selectionModel) => {
          if (selectionModel.length <= maxSelectableRowCount) {
            setSelectedItems(selectionModel);

            return onRowSelection(
              selectionModel.map((id) => gridRows.find((row) => row.id === id) || { id })
            );
          }

          return null;
        }}
        selectionModel={selectedItems}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
        onRowEditStart={handleOnRowEditModeChange}
        onRowEditStop={handleOnRowEditModeChange}
        processRowUpdate={processRowUpdate}
        isRowSelectable={isRowSelectable}
        components={{
          Footer: () => (
            <Box
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                padding: "10px",
                flexDirection: "column",
                borderTop: "1px solid #ddd",
              }}
            >
              {!hideExportButton && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    borderBottom: "1px solid #ddd",
                    pb: 1,
                    [mobileMediaQuery]: { flexDirection: "column" },
                  }}
                >
                  {footerLeftButton}
                  {footerRightButton}
                </Box>
              )}
              {!disablePagination && (
                <PaginationComponent
                  sx={{ pt: 1 }}
                  page={tablePaging.page - 1}
                  pageSize={tablePaging.pageSize}
                  totalCount={remotePaging?.totalCount ?? rows.length}
                  rowsPerPageOptions={rowsPerPageOptions}
                  onPageChange={(page) =>
                    setTablePaging((prev) => ({
                      ...prev,
                      page: page + 1,
                      status: "inside",
                    }))
                  }
                  onPageSizeChange={(pageSize) =>
                    setTablePaging({
                      page: 1,
                      pageSize,
                      status: "inside",
                    })
                  }
                />
              )}
            </Box>
          ),
          Toolbar: !hideToolBar ? PaginationModel : undefined,
          LoadingOverlay,
          Pagination: PaginationComponent,
        }}
        onCellEditCommit={onCellEditCommit}
        experimentalFeatures={{ newEditingApi: true }}
        componentsProps={{
          filterPanel: {
            sx: {
              "& .MuiButton-startIcon": {
                display: "none !important",
              },
            },
          },
          panel: {
            anchorEl: filterButtonEl,
            sx: {
              "& .MuiDataGrid-panelWrapper": {
                pt: 2,
              },
              "& .MuiDataGrid-panelHeader": {
                display: "none",
              },
            },
          },
          toolbar: toolbarProps,
          pagination: {
            rowsPerPageOptions,
          },
        }}
        sx={{
          "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
            display: disableSelectAllCheckbox ? "none" : "flex",
          },
          border: "none",
          ...theme.dataGrid[paginationModel].pagination,
        }}
      />
      {editingDialog.row && (
        <Dialog
          title={t("edit")}
          open={!!editingDialog.row}
          onClose={handleCloseEditingDialog}
          defaultTitle
        >
          {EditForm ? (
            <EditForm row={editingDialog.row} />
          ) : (
            <DefaultEditForm
              row={editingDialog.row}
              columns={columns}
              handleRowUpdate={handleRowUpdate}
            />
          )}
        </Dialog>
      )}
    </Box>
  );
}

DataTable.renderCellExpand = renderCellExpand;

DataTable.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  remotePaging: PropTypes.object,
  loadData: PropTypes.func,
  editMode: PropTypes.string,
  hideToolBar: PropTypes.bool,
  hideSearchBox: PropTypes.bool,
  height: PropTypes.string,
  density: PropTypes.string,
  autoHeight: PropTypes.bool,
  getRowId: PropTypes.func,
  checkboxSelection: PropTypes.bool,
  hideFooterSelectedRowCount: PropTypes.bool,
  onRowSelection: PropTypes.func,
  disableInitialLoad: PropTypes.bool,
  onCellEditCommit: PropTypes.func,
  isRowSelectable: PropTypes.func,
  pagination: PropTypes.bool,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  initialPagination: PropTypes.object,
  hideFilterButton: PropTypes.bool,
  hideExportButton: PropTypes.bool,
  disableLoadingAnimation: PropTypes.bool,
  onRowSave: PropTypes.func,
  EditForm: PropTypes.func,
  paginationModel: PropTypes.string,
  toolbarModel: PropTypes.string,
  FilterForm: PropTypes.any,
  footerLeftButton: PropTypes.any,
  footerRightButton: PropTypes.any,
  dataTableTitle: PropTypes.string,
  disableSelectionOnClick: PropTypes.bool,
  disableColumnMenu: PropTypes.bool,

  disableSelectAllCheckbox: PropTypes.bool,
  onRowClick: PropTypes.func,
  disablePagination: PropTypes.bool,
  maxSelectableRowCount: PropTypes.number,
};
